import { ReactElement } from 'react'

import { Link, Text, typography } from '@joor/design-system'
import styled from 'styled-components'

import { PremiumFeaturesQuery } from '__generated__/atlas-types'

type PremiumFeatureFromQuery = NonNullable<
  PremiumFeaturesQuery['premiumFeatures']
>[0]

const Wrapper = styled.div`
  display: grid;
  grid-template-columns: minmax(180px, 320px) 2fr 1fr;
  padding: 16px;
  align-items: center;
  justify-content: center;
  gap: 40px;
  width: 100%;

  @media (max-width: 1180px) {
    grid-template-columns: 1fr;
    grid-template-rows: auto auto;
    gap: 12px;
    padding: 12px;
    align-items: start;
  }
`

const ImageContainer = styled.div`
  width: 100%;
  min-width: 180px;
  max-width: 320px;
  height: 213px;
  background: var(--color-neutral-100);
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }

  @media (max-width: 1180px) {
    min-width: 100%;
    max-width: 100%;
    height: 120px;
  }
`

const InfoAndPriceWrapper = styled.div`
  display: contents;

  @media (max-width: 1180px) {
    display: grid;
    grid-template-columns: 1.5fr 1fr;
    gap: 12px;
  }
`

const Info = styled.div`
  display: grid;
  grid-template-rows: auto 1fr;
  align-items: start;
  height: 100%;

  .feature-description {
    white-space: pre-wrap !important;
  }

  @media (max-width: 1180px) {
    height: auto;
  }
`

const NameContainer = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 12px;

  h2 {
    margin-bottom: 0;
  }

  a {
    color: var(--color-secondary-800) !important;
    white-space: nowrap !important;
    font-size: var(--size-font-small);
    &:hover {
      text-decoration: underline;
    }
    &:active {
      color: var(--color-secondary-800) !important;
    }
  }
`

const LinkedName = styled(Link)`
  &&& {
    ${typography.alpha.primaryActive.headline2}
    font-weight: lighter;
    color: var(--color-secondary-800);
    line-height: 1.25;
  }
`

const Name = styled.h2`
  &&& {
    ${typography.alpha.primaryActive.headline2}
    font-weight: lighter;
    color: var(--color-primary-800);
    line-height: 1.25;
  }
`

const truncateText = (name: string, length = 25) =>
  name?.length > length ? name.substring(0, length).trim() + '...' : name

export const Content = ({
  children,
  feature,
  index,
}: {
  children: ReactElement
  feature: PremiumFeatureFromQuery
  index: number
}) => {
  const truncatedName = truncateText(feature.name, 55)
  const marketingUrl = feature?.marketingUrl
  const showLearnMore = !!marketingUrl

  return (
    <Wrapper>
      {feature?.image && (
        <ImageContainer>
          <img
            className="feature-image"
            alt={'feat' + index}
            src={feature.image}
          />
        </ImageContainer>
      )}
      <InfoAndPriceWrapper>
        <Info>
          <NameContainer>
            {feature?.internalUrl ? (
              <LinkedName
                title={
                  feature.name && feature.name.length > 55 ? feature.name : ''
                }
                href={feature.internalUrl!}
              >
                {truncatedName}{' '}
              </LinkedName>
            ) : (
              <Name
                title={
                  feature.name && feature.name.length > 55 ? feature.name : ''
                }
              >
                {truncatedName}{' '}
              </Name>
            )}
            {showLearnMore && (
              <Link href={marketingUrl} target="_blank" color="secondary-800">
                Learn More
              </Link>
            )}
          </NameContainer>
          <Text
            className="feature-description"
            letterSpacing="tight"
            color="primary-500"
            title={
              feature.description && feature.description.length > 195
                ? feature.description
                : ''
            }
          >
            {feature.description && truncateText(feature.description, 195)}
          </Text>
        </Info>
        {children}
      </InfoAndPriceWrapper>
    </Wrapper>
  )
}
